/*
 * Token Registry & Audit — iter303i
 * ==================================
 * Admin surface for the central `{tokens}` library that powers every
 * letter/report in the app. Two tabs:
 *
 *   • Library — browse / filter / add / edit / delete tokens
 *   • Audit   — one-button sweep across ALL letter surfaces (36 quote
 *               types × 22 fields + 8 status letters × 9 cells). Buckets
 *               into: live-in-use / draft-in-use / unregistered drafts
 *               / unused registry entries. Click any surface to jump
 *               back to the Script Audit Studio.
 *
 * Backend routes live under /api/token-registry and /api/token-audit.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Plus, Edit2, Trash2, Copy, X, Check, AlertCircle, Search, ExternalLink, GitMerge, Plug } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });
api.interceptors.request.use(cfg => {
  const tok = typeof window !== 'undefined' ? localStorage.getItem('access_token') : '';
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

const GROUPS = ['Client', 'Property', 'Roof', 'Appraisal', 'Financial', 'Invoice', 'Rep', 'Custom'];
const STATUS_BADGES = {
  live:       { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: '🟢 Live' },
  draft:      { color: 'bg-amber-100 text-amber-800 border-amber-300',       label: '🟡 Draft' },
  deprecated: { color: 'bg-slate-200 text-slate-600 border-slate-300',       label: '💤 Deprecated' },
};

export default function TokenRegistryPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('library');

  return (
    <div className="min-h-screen bg-[#F8F7F4] p-6" data-testid="token-registry-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button data-testid="back-btn" onClick={() => navigate('/script-audit')} className="p-1 rounded hover:bg-slate-200"><ArrowLeft size={16} /></button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Token Registry &amp; Audit</h1>
            <p className="text-sm text-slate-600">Central library of every <code className="text-[11px] bg-slate-100 px-1 rounded">{'{token}'}</code> used across letters + audit sweep across all surfaces.</p>
          </div>
        </div>

        <div className="flex gap-1 bg-slate-800 rounded-md p-1 mb-4 w-fit" data-testid="tab-selector">
          {['library','audit'].map(t => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-300 hover:text-white'}`}>
              {t === 'library' ? 'Library' : 'Audit Sweep'}
            </button>
          ))}
        </div>

        {tab === 'library' ? <LibraryTab /> : <AuditTab />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library tab — CRUD table
// ---------------------------------------------------------------------------
function LibraryTab() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [filterGroup, setFilterGroup] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [mergingKey, setMergingKey] = useState(null);  // iter303k — Merge/rename modal
  const [wiringKey, setWiringKey] = useState(null);    // iter303l — Wire-to-field modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/token-registry');
      setTokens(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return tokens.filter(t => {
      if (filterGroup !== 'All' && t.group !== filterGroup) return false;
      if (filterStatus !== 'All' && t.status !== filterStatus) return false;
      if (filterText) {
        const q = filterText.toLowerCase();
        return (t.key || '').toLowerCase().includes(q)
            || (t.label || '').toLowerCase().includes(q)
            || (t.description || '').toLowerCase().includes(q)
            || (t.data_source || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [tokens, filterText, filterGroup, filterStatus]);

  const remove = async (key) => {
    if (!window.confirm(`Delete token {${key}}?`)) return;
    try { await api.delete(`/token-registry/${encodeURIComponent(key)}`); load(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const copyChip = (key) => { try { navigator.clipboard.writeText(`{${key}}`); } catch { /* clipboard blocked */ } };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-2 text-slate-400" />
          <input data-testid="filter-text" value={filterText} onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter by name/label/description…"
            className="pl-6 pr-2 py-1 text-xs border border-slate-300 rounded w-64" />
        </div>
        <select data-testid="filter-group" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}
          className="text-xs border border-slate-300 rounded px-2 py-1">
          <option value="All">All groups</option>
          {GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select data-testid="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="text-xs border border-slate-300 rounded px-2 py-1">
          <option value="All">All statuses</option>
          <option value="live">🟢 Live</option>
          <option value="draft">🟡 Draft</option>
          <option value="deprecated">💤 Deprecated</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} / {tokens.length} tokens</span>
        <button data-testid="add-token-btn" onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-white bg-[#316536] hover:bg-[#265127] rounded"><Plus size={12} /> Add Draft Token</button>
      </div>
      {loading ? <div className="text-sm text-slate-500 p-6 text-center">Loading…</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="library-table">
            <thead className="bg-slate-100 text-slate-700 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5 font-bold">Key</th>
                <th className="text-left px-2 py-1.5 font-bold">Label</th>
                <th className="text-left px-2 py-1.5 font-bold">Group</th>
                <th className="text-left px-2 py-1.5 font-bold">Status</th>
                <th className="text-left px-2 py-1.5 font-bold">Description</th>
                <th className="text-left px-2 py-1.5 font-bold">Data Source (how it&apos;s generated)</th>
                <th className="text-left px-2 py-1.5 font-bold">Sample</th>
                <th className="text-right px-2 py-1.5 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const badge = STATUS_BADGES[t.status] || STATUS_BADGES.draft;
                return (
                  <tr key={t.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`token-row-${t.key}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px] font-bold">{'{' + t.key + '}'}</td>
                    <td className="px-2 py-1.5">{t.label}</td>
                    <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px]">{t.group}</span></td>
                    <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${badge.color}`}>{badge.label}</span></td>
                    <td className="px-2 py-1.5 max-w-[220px] text-slate-600">{t.description}</td>
                    <td className="px-2 py-1.5 max-w-[280px] text-slate-600 font-mono text-[10px]">{t.data_source || '—'}</td>
                    <td className="px-2 py-1.5 text-slate-500">{t.sample || '—'}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button data-testid={`copy-${t.key}`} onClick={() => copyChip(t.key)} title="Copy {token}" className="p-1 text-slate-500 hover:text-slate-800"><Copy size={12} /></button>
                      <button data-testid={`edit-${t.key}`} onClick={() => setEditingKey(t.key)} title="Edit" className="p-1 text-slate-500 hover:text-[#D35400]"><Edit2 size={12} /></button>
                      <button data-testid={`wire-${t.key}`} onClick={() => setWiringKey(t.key)} title={t.status === 'live' ? 'Re-wire this token' : 'Wire to a field / mark Live'} className="p-1 text-slate-500 hover:text-emerald-600"><Plug size={12} /></button>
                      {t.status !== 'live' && (
                        <button data-testid={`merge-${t.key}`} onClick={() => setMergingKey(t.key)} title="Merge / rename into another token" className="p-1 text-slate-500 hover:text-blue-600"><GitMerge size={12} /></button>
                      )}
                      {t.status !== 'live' && (
                        <button data-testid={`delete-${t.key}`} onClick={() => remove(t.key)} title="Delete draft" className="p-1 text-red-500 hover:text-red-700"><Trash2 size={12} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {showAddModal && <TokenEditModal onClose={() => setShowAddModal(false)} onSaved={() => { setShowAddModal(false); load(); }} />}
      {editingKey && <TokenEditModal editKey={editingKey} token={tokens.find(t => t.key === editingKey)} onClose={() => setEditingKey(null)} onSaved={() => { setEditingKey(null); load(); }} />}
      {mergingKey && <MergeModal sourceKey={mergingKey} allTokens={tokens} onClose={() => setMergingKey(null)} onDone={() => { setMergingKey(null); load(); }} />}
      {wiringKey && <WireModal tokenKey={wiringKey} token={tokens.find(t => t.key === wiringKey)} onClose={() => setWiringKey(null)} onDone={() => { setWiringKey(null); load(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add / Edit modal
// ---------------------------------------------------------------------------
function TokenEditModal({ editKey, token, onClose, onSaved }) {
  const isEdit = !!editKey;
  const [form, setForm] = useState({
    key:         token?.key || '',
    label:       token?.label || '',
    group:       token?.group || 'Custom',
    description: token?.description || '',
    data_source: token?.data_source || '',
    sample:      token?.sample || '',
    status:      token?.status || 'draft',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true); setError('');
    try {
      if (isEdit) await api.put(`/token-registry/${encodeURIComponent(editKey)}`, form);
      else        await api.post('/token-registry', form);
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="token-edit-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold">{isEdit ? `Edit {${editKey}}` : 'Add Draft Token'}</h2>
          <button data-testid="modal-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        {error && <div className="mb-2 px-2 py-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded flex items-center gap-1"><AlertCircle size={12} />{error}</div>}
        <div className="space-y-3 text-xs">
          <Field label="Key (letters/numbers/underscore only)" testId="field-key">
            <input value={form.key} disabled={isEdit} onChange={(e) => setForm(f => ({ ...f, key: e.target.value }))} className="w-full border rounded px-2 py-1 font-mono disabled:bg-slate-100" placeholder="e.g. Deposit_received_date" />
          </Field>
          <Field label="Label (human-readable)" testId="field-label">
            <input value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} className="w-full border rounded px-2 py-1" placeholder="Deposit Received Date" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Group" testId="field-group">
              <select value={form.group} onChange={(e) => setForm(f => ({ ...f, group: e.target.value }))} className="w-full border rounded px-2 py-1">
                {GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Status" testId="field-status">
              <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="w-full border rounded px-2 py-1">
                <option value="draft">🟡 Draft (needs backend wiring)</option>
                <option value="live">🟢 Live (backend implemented)</option>
                <option value="deprecated">💤 Deprecated</option>
              </select>
            </Field>
          </div>
          <Field label="Description (what the token resolves to)" testId="field-description">
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full border rounded px-2 py-1" placeholder="Date the deposit cleared into our bank account" />
          </Field>
          <Field label="Data Source (how it's generated)" testId="field-data-source">
            <textarea value={form.data_source} onChange={(e) => setForm(f => ({ ...f, data_source: e.target.value }))} rows={2} className="w-full border rounded px-2 py-1 font-mono text-[10px]" placeholder="e.g. appraisal.deposit_received_date  |  or plain-english TBD note" />
          </Field>
          <Field label="Sample value" testId="field-sample">
            <input value={form.sample} onChange={(e) => setForm(f => ({ ...f, sample: e.target.value }))} className="w-full border rounded px-2 py-1" placeholder="05 Aug 2026" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          <button data-testid="modal-save-btn" onClick={submit} disabled={busy || !form.key.trim()} className="px-3 py-1 text-xs font-bold text-white bg-[#316536] hover:bg-[#265127] rounded disabled:opacity-50 inline-flex items-center gap-1"><Check size={12} /> Save</button>
        </div>
      </div>
    </div>
  );
}
function Field({ label, testId, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold" data-testid={testId}>{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Merge / rename modal (Phase A — iter303k)
// ---------------------------------------------------------------------------
function MergeModal({ sourceKey, allTokens, onClose, onDone }) {
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Only LIVE tokens are eligible merge targets (drafts merging into
  // other drafts is rarely what an operator means and just kicks the
  // can down the road).
  const targetOptions = useMemo(() =>
    allTokens.filter(t => t.status === 'live' && t.key !== sourceKey), [allTokens, sourceKey]
  );

  const doPreview = async () => {
    setBusy(true); setError(''); setPreview(null);
    try {
      const { data } = await api.post(`/token-registry/${encodeURIComponent(sourceKey)}/preview-merge`, { target_key: target });
      setPreview(data);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const doMerge = async () => {
    if (!window.confirm(`Rewrite ${preview.cells_touched} cell${preview.cells_touched > 1 ? 's' : ''} — replace {${sourceKey}} with {${target}} across all letters?`)) return;
    setBusy(true); setError('');
    try {
      await api.post(`/token-registry/${encodeURIComponent(sourceKey)}/merge-into`, { target_key: target });
      onDone();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="merge-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold flex items-center gap-2"><GitMerge size={16} /> Merge / Rename Token</h2>
          <button data-testid="merge-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <p className="text-xs text-slate-600 mb-4">
          Rewrites every letter cell containing <code className="font-mono bg-slate-100 px-1 rounded">{`{${sourceKey}}`}</code> to use the target token below, then deletes the draft <code className="font-mono">{`{${sourceKey}}`}</code>. Ideal for fixing typos (e.g. <code className="font-mono">{'{representative_name}'}</code> → <code className="font-mono">{'{rep_name}'}</code>).
        </p>
        {error && <div className="mb-2 px-2 py-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded flex items-center gap-1"><AlertCircle size={12} />{error}</div>}
        <div className="space-y-3 text-xs">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Merge into (LIVE tokens only)</span>
            <select data-testid="merge-target-select" value={target} onChange={(e) => { setTarget(e.target.value); setPreview(null); }} className="w-full border rounded px-2 py-1 mt-1">
              <option value="">— Choose target —</option>
              {targetOptions.map(t => (
                <option key={t.key} value={t.key}>{'{'}{t.key}{'}'}  ·  {t.label}</option>
              ))}
            </select>
          </label>
          <div className="flex gap-2">
            <button data-testid="merge-preview-btn" onClick={doPreview} disabled={!target || busy}
              className="px-3 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">Preview changes</button>
            {preview && (
              <button data-testid="merge-commit-btn" onClick={doMerge} disabled={busy || preview.cells_touched === 0}
                className="px-3 py-1 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 inline-flex items-center gap-1"><Check size={12} /> Merge {preview.cells_touched} cell{preview.cells_touched > 1 ? 's' : ''}</button>
            )}
          </div>
          {preview && (
            <div className="border border-slate-200 rounded p-2 bg-slate-50/60" data-testid="merge-preview-report">
              <div className="text-[11px] font-bold text-slate-700 mb-1">Preview report</div>
              <ul className="text-[11px] space-y-0.5">
                <li><b>Cells touched:</b> {preview.cells_touched}</li>
                <li><b>Total {'{token}'} occurrences replaced:</b> {preview.occurrences_replaced}</li>
              </ul>
              {preview.examples.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-2 mb-1">First {preview.examples.length} affected cells</div>
                  <ul className="text-[10px] space-y-1">
                    {preview.examples.map((e, i) => (
                      <li key={i} className="bg-white border border-slate-100 rounded px-2 py-1">
                        <span className="font-mono text-slate-500">{e.surface}</span>
                        <span className="text-slate-400"> › </span>
                        <span className="font-mono text-slate-500">{e.field}</span>
                        <span className="ml-2 px-1 rounded bg-blue-100 text-blue-800">×{e.occurrences}</span>
                        <div className="mt-0.5 text-slate-600 italic">{e.snippet}…</div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {preview.cells_touched === 0 && <div className="text-[11px] text-slate-500 italic">No cells contain <code className="font-mono bg-white px-1 rounded">{`{${sourceKey}}`}</code>. Nothing to rewrite — you can still just delete the draft.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Wire-to-field modal (Phase B — iter303l)
// ---------------------------------------------------------------------------
const CONFIDENCE_BADGE = {
  high:   { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: '🟢 High confidence' },
  medium: { color: 'bg-amber-100 text-amber-800 border-amber-300',       label: '🟡 Medium confidence' },
  low:    { color: 'bg-orange-100 text-orange-800 border-orange-300',    label: '🟠 Low confidence' },
  guess:  { color: 'bg-slate-100 text-slate-600 border-slate-300',       label: '⚪ Starting pathway (no description)' },
};

function WireModal({ tokenKey, token, onClose, onDone }) {
  const [suggestion, setSuggestion] = useState(null);
  const [schema, setSchema] = useState({});     // { collection: { fields[], description } }
  const [sampleAppraisals, setSampleAppraisals] = useState([]);
  const [sampleAppraisalId, setSampleAppraisalId] = useState('');
  const [collection, setCollection] = useState('');
  const [path, setPath] = useState('');
  const [fallback, setFallback] = useState('');
  const [fmt, setFmt] = useState('text');
  const [prefix, setPrefix] = useState('');
  const [samplePreview, setSamplePreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [sugRes, schRes] = await Promise.all([
          api.get(`/token-registry/${encodeURIComponent(tokenKey)}/wire-suggestion`),
          api.get('/token-registry-schema'),
        ]);
        setSuggestion(sugRes.data);
        setSchema(schRes.data || {});
        // Extract sample_appraisals + delete from schema map (works whether the backend uses `sample_appraisals` or the legacy `_sample_appraisals`).
        const rawSchema = { ...(schRes.data || {}) };
        const samples = rawSchema.sample_appraisals || rawSchema._sample_appraisals || [];
        delete rawSchema.sample_appraisals; delete rawSchema._sample_appraisals;
        setSchema(rawSchema);
        setSampleAppraisals(samples);
        if (samples.length) setSampleAppraisalId(samples[0].id);
        // iter303n — Re-wire flow: pre-fill from the token's existing
        // resolver so operators tweak instead of restarting.
        if (token?.resolver_type === 'field' && token?.resolver_source) {
          setCollection(token.resolver_source);
          setPath(token.resolver_path || '');
          setFallback(token.resolver_fallback || '');
          setFmt(token.resolver_format || 'text');
          setPrefix(token.resolver_prefix || '');
        } else {
          setCollection(sugRes.data.collection);
          setPath(sugRes.data.path);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [tokenKey, token]);

  // iter303m — Live preview against the selected sample appraisal.
  // Debounced so typing in the path field doesn't hammer the endpoint.
  useEffect(() => {
    if (!collection || !path || !sampleAppraisalId) { setSamplePreview(''); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/token-registry/preview-value', { params: { collection, path, appraisal_id: sampleAppraisalId, fmt, prefix } });
        setSamplePreview(data?.value ?? '');
      } catch { setSamplePreview(''); }
    }, 250);
    return () => clearTimeout(t);
  }, [collection, path, sampleAppraisalId, fmt, prefix]);

  const availableFields = schema[collection]?.fields || [];

  const acceptSuggestion = () => {
    if (!suggestion) return;
    setCollection(suggestion.collection);
    setPath(suggestion.path);
  };

  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.put(`/token-registry/${encodeURIComponent(tokenKey)}/wire`, { collection, path, fallback, format: fmt, prefix });
      onDone();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const confBadge = CONFIDENCE_BADGE[suggestion?.confidence] || CONFIDENCE_BADGE.guess;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="wire-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold flex items-center gap-2"><Plug size={16} /> Wire <code className="font-mono text-sm">{`{${tokenKey}}`}</code></h2>
          <button data-testid="wire-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        {token?.description && <p className="text-[11px] text-slate-600 italic mb-2">Description: {token.description}</p>}
        {/* iter303m — Sample-appraisal preview picker */}
        {sampleAppraisals.length > 0 && (
          <label className="block mb-3 text-xs bg-slate-50 border border-slate-200 rounded p-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Preview values against</span>
            <select data-testid="wire-sample-appraisal" value={sampleAppraisalId} onChange={(e) => setSampleAppraisalId(e.target.value)}
              className="w-full border rounded px-2 py-1 mt-1 text-xs">
              {sampleAppraisals.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </label>
        )}
        {error && <div className="mb-2 px-2 py-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded flex items-center gap-1"><AlertCircle size={12} />{error}</div>}
        {!suggestion ? <div className="text-sm text-slate-500 py-4 text-center">Loading suggestion + schema…</div> : (
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-emerald-200 bg-emerald-50/40 rounded p-3" data-testid="wire-suggestion-pane">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">💡 Suggested path</div>
              <span className={`inline-block text-[10px] px-2 py-0.5 rounded border font-bold ${confBadge.color}`}>{confBadge.label}</span>
              <div className="mt-2 font-mono text-sm">{suggestion.collection}.<span className="text-emerald-700">{suggestion.path}</span></div>
              <div className="text-[10px] text-slate-500 mt-1 italic">{suggestion.reason}</div>
              <button data-testid="wire-accept-suggestion" onClick={acceptSuggestion}
                className="mt-3 w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded">
                <Check size={12} /> Accept &amp; use →
              </button>
            </div>
            <div className="border border-slate-200 rounded p-3" data-testid="wire-manual-pane">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">🧭 Or navigate manually</div>
              <label className="block mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Collection</span>
                <select data-testid="wire-collection-select" value={collection} onChange={(e) => { setCollection(e.target.value); setPath(''); }} className="w-full border rounded px-2 py-1 mt-1 text-xs">
                  <option value="">— Choose —</option>
                  {Object.keys(schema).map(c => <option key={c} value={c}>{c}  ·  {schema[c].description}</option>)}
                </select>
              </label>
              <label className="block mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Field path</span>
                <input data-testid="wire-path-input" list="wire-fields-list" value={path} onChange={(e) => setPath(e.target.value)} className="w-full border rounded px-2 py-1 mt-1 text-xs font-mono" placeholder="e.g. name_first" />
                <datalist id="wire-fields-list">
                  {availableFields.map(f => <option key={f} value={f} />)}
                </datalist>
                {availableFields.length > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1">{availableFields.length} fields available (autocomplete when you type)</div>
                )}
              </label>
              <label className="block mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Fallback if empty</span>
                <input data-testid="wire-fallback-input" value={fallback} onChange={(e) => setFallback(e.target.value)} className="w-full border rounded px-2 py-1 mt-1 text-xs" placeholder="(optional)" />
              </label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Format</span>
                  <select data-testid="wire-format-select" value={fmt} onChange={(e) => setFmt(e.target.value)} className="w-full border rounded px-2 py-1 mt-1 text-xs bg-white">
                    <option value="text">Text (as stored)</option>
                    <option value="money">Money ($1,234.50)</option>
                    <option value="date">Date (dd/mm/yyyy)</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Prefix</span>
                  <input data-testid="wire-prefix-input" value={prefix} maxLength={10} onChange={(e) => setPrefix(e.target.value)} className="w-full border rounded px-2 py-1 mt-1 text-xs" placeholder="e.g. C" />
                </label>
              </div>
              <div className="text-[11px] text-slate-700 mt-2 mb-3 bg-white border border-slate-200 rounded px-2 py-1"><b>Live sample:</b> <span className="italic">{samplePreview || '(empty)'}</span></div>
              <button data-testid="wire-save-btn" onClick={save} disabled={busy || !collection || !path}
                className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-bold text-white bg-[#316536] hover:bg-[#265127] rounded disabled:opacity-50">
                <Check size={12} /> Save &amp; mark Live
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Audit tab — full sweep report
// ---------------------------------------------------------------------------
function AuditTab() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/token-audit');
      setReport(data);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { run(); }, [run]);

  const registerAsDraft = async (key) => {
    try {
      await api.post('/token-registry', { key, label: key, group: 'Custom', status: 'draft', description: '', data_source: '— TBD — added from audit sweep' });
      run();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const fixName = async (t) => {
    const target = window.prompt(
      `Rename {${t.key}} everywhere it is used.\nNew token name (letters, numbers and _ only):`,
      t.suggested_key || '',
    );
    if (!target) return;
    try {
      const { data: dry } = await api.post('/token-registry/fix-name', { key: t.key, target_key: target, dry_run: true });
      if (!window.confirm(`Replace {${t.key}} with {${dry.target_key}} in ${dry.cells_touched} cell(s) (${dry.occurrences_replaced} occurrence(s))?`)) return;
      await api.post('/token-registry/fix-name', { key: t.key, target_key: target });
      run();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const mergeInto = async (t) => {
    const target = window.prompt(`Merge {${t.key}} into which existing token?\nType the exact token name (e.g. representative_name):`, '');
    if (!target) return;
    try {
      const { data: dry } = await api.post(`/token-registry/${encodeURIComponent(t.key)}/preview-merge`, { target_key: target.trim() });
      if (!window.confirm(`Replace {${t.key}} with {${target.trim()}} in ${dry.cells_touched} cell(s) (${dry.occurrences_replaced} occurrence(s))? This can't be undone.`)) return;
      await api.post(`/token-registry/${encodeURIComponent(t.key)}/merge-into`, { target_key: target.trim() });
      run();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const renderMerge = (t) => (
    <button data-testid={`audit-merge-${t.key}`} onClick={() => mergeInto(t)}
      className="text-[10px] px-2 py-0.5 font-bold text-white bg-blue-600 hover:bg-blue-700 rounded whitespace-nowrap">Merge into…</button>
  );
  const renderFix = (t) => t.invalid_name ? (
    <span className="inline-flex items-center gap-1">
      <span data-testid={`invalid-name-${t.key}`} className="text-[10px] px-1.5 py-0.5 font-bold text-red-700 bg-red-50 border border-red-200 rounded whitespace-nowrap">Invalid name</span>
      <button data-testid={`fix-name-${t.key}`} onClick={() => fixName(t)}
        className="text-[10px] px-2 py-0.5 font-bold text-white bg-red-600 hover:bg-red-700 rounded whitespace-nowrap">Fix name</button>
    </span>
  ) : null;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3">
        <button data-testid="run-audit-btn" onClick={run} disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-[#316536] hover:bg-[#265127] rounded disabled:opacity-50">
          <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> {busy ? 'Scanning…' : 'Run Audit Sweep'}
        </button>
        {report && (
          <span className="text-xs text-slate-500">
            Last run <b>{new Date(report.generated_at).toLocaleString()}</b> — scanned <b>{report.surface_count}</b> letter surfaces against <b>{report.registry_total}</b> registered tokens.
          </span>
        )}
        {error && <span className="text-xs text-red-600 ml-auto">{error}</span>}
      </div>

      {report && (
        <>
          {/* iter303m — Broken wiring section — surfaces LIVE tokens whose
              resolver_path doesn't exist on the model OR returns empty
              from the latest appraisal. */}
          {report.broken_wiring && report.broken_wiring.length > 0 && (
            <div data-testid="section-broken-wiring" className="border rounded-md p-4 border-red-300 bg-red-50/40">
              <h3 className="text-sm font-bold text-red-800">🔧 Live tokens with broken wiring ({report.broken_wiring.length})</h3>
              <p className="text-[11px] text-slate-600 mb-2">These tokens are marked LIVE but their wiring doesn&apos;t resolve. Open Library, click 🔌 Wire, and re-point them.</p>
              <div className="space-y-1">
                {report.broken_wiring.map(t => (
                  <div key={t.key} className="bg-white border border-red-200 rounded px-2 py-1.5 text-[11px]">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold">{`{${t.key}}`}</span>
                      <span className="text-slate-500">→</span>
                      <span className="font-mono text-slate-600">{t.resolver_source}.{t.resolver_path}</span>
                    </div>
                    <ul className="ml-4 mt-0.5 list-disc text-red-700">
                      {t.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 1 — Unregistered drafts (the master TODO) */}
          <AuditSection
            testId="section-unregistered"
            title={`⚠ Unregistered draft tokens (${report.unregistered_drafts.length})`}
            hint="These braces appear in letter cells but the registry doesn't know about them. Click 'Register as draft' to add each one to the Library so future-you (and the dev) can track what they should resolve to."
            color="border-amber-300 bg-amber-50/40"
            rows={report.unregistered_drafts}
            renderExtra={(t) => (
              <span className="inline-flex items-center gap-1">
                {t.invalid_name ? renderFix(t) : (
                  <button data-testid={`register-${t.key}`} onClick={() => registerAsDraft(t.key)}
                    className="text-[10px] px-2 py-0.5 font-bold text-white bg-amber-600 hover:bg-amber-700 rounded whitespace-nowrap">Register as draft</button>
                )}
                {renderMerge(t)}
              </span>
            )}
            navigate={navigate}
          />

          {/* Section 2 — Registered drafts in use */}
          <AuditSection
            testId="section-draft-in-use"
            title={`🟡 Registered draft tokens in use (${report.draft_in_use.length})`}
            hint="Registered as drafts, still awaiting backend wiring. Prints raw braces on the PDF until wired."
            color="border-yellow-300 bg-yellow-50/30"
            rows={report.draft_in_use}
            renderExtra={(t) => <span className="inline-flex items-center gap-1">{renderFix(t)}{renderMerge(t)}</span>}
            navigate={navigate}
          />

          {/* Section 3 — Live in use */}
          <AuditSection
            testId="section-live-in-use"
            title={`✅ Live tokens in use (${report.live_in_use.length})`}
            hint="These resolve correctly on the PDF. Sorted by usage count."
            color="border-emerald-300 bg-emerald-50/30"
            rows={report.live_in_use}
            navigate={navigate}
          />

          {/* Section 4 — Unused registry entries */}
          <div data-testid="section-unused" className={`border rounded-md p-4 border-slate-200 bg-slate-50/50`}>
            <h3 className="text-sm font-bold text-slate-700 mb-1">💤 Unused registry entries ({report.unused_registry_entries.length})</h3>
            <p className="text-[11px] text-slate-500 mb-2">Defined but no surface uses them. Candidates for pruning or spelling-fix (e.g. `Property_Address` vs `property_address`).</p>
            <div className="flex flex-wrap gap-1">
              {report.unused_registry_entries.map(t => (
                <span key={t.key} className="text-[10px] px-2 py-0.5 bg-white border border-slate-300 rounded font-mono">{`{${t.key}}`}</span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AuditSection({ testId, title, hint, color, rows, renderExtra, navigate }) {
  const [expanded, setExpanded] = useState(null);
  if (rows.length === 0) return null;
  return (
    <div data-testid={testId} className={`border rounded-md p-4 ${color}`}>
      <h3 className="text-sm font-bold text-slate-700">{title}</h3>
      <p className="text-[11px] text-slate-500 mb-2">{hint}</p>
      <div className="space-y-1">
        {rows.map(t => (
          <div key={t.key} className="bg-white border border-slate-200 rounded">
            <div className="flex items-center gap-2 px-2 py-1.5">
              <button data-testid={`expand-${t.key}`} onClick={() => setExpanded(expanded === t.key ? null : t.key)} className="text-[10px] text-slate-500 hover:text-slate-800">{expanded === t.key ? '▼' : '▶'}</button>
              <span className="font-mono text-[11px] font-bold">{`{${t.key}}`}</span>
              <span className="text-[10px] text-slate-500">used in {t.usage_count} cell{t.usage_count > 1 ? 's' : ''}</span>
              <div className="ml-auto flex items-center gap-1">
                {renderExtra && renderExtra(t)}
              </div>
            </div>
            {expanded === t.key && (
              <div className="border-t border-slate-100 px-3 py-2 bg-slate-50/60 text-[11px]">
                <ul className="space-y-0.5">
                  {t.usages.map((u, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-slate-600">{u.surface}</span>
                      <span className="text-slate-400">›</span>
                      <span className="text-slate-500 font-mono">{u.field}</span>
                      {u.route && (
                        <button onClick={() => navigate(u.route)} title="Open in Script Audit Studio" className="ml-auto text-slate-400 hover:text-[#D35400]"><ExternalLink size={11} /></button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
